/**
 * Mega Menu Navigation JavaScript
 * Handles interactive functionality and responsive behavior
 */

class MegaMenuNavigation {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupResponsiveBehavior();
    this.setupAccessibility();
  }

  bindEvents() {
    // Handle window resize for responsive behavior
    window.addEventListener('resize', this.debounce(this.handleResize.bind(this), 250));
    
    // Handle keyboard navigation
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
    
    // Handle touch events for mobile
    if ('ontouchstart' in window) {
      this.setupTouchEvents();
    }
  }

  setupResponsiveBehavior() {
    const menu = document.querySelector('.mega-menu-navigation');
    if (!menu) return;

    // Add responsive classes based on screen size
    this.updateResponsiveClasses();
  }

  updateResponsiveClasses() {
    const menu = document.querySelector('.mega-menu-navigation');
    if (!menu) return;

    const width = window.innerWidth;
    
    // Remove existing responsive classes
    menu.classList.remove('mega-menu-navigation--mobile', 'mega-menu-navigation--tablet', 'mega-menu-navigation--desktop');
    
    // Add appropriate responsive class
    if (width <= 480) {
      menu.classList.add('mega-menu-navigation--mobile');
    } else if (width <= 768) {
      menu.classList.add('mega-menu-navigation--tablet');
    } else {
      menu.classList.add('mega-menu-navigation--desktop');
    }
  }

  setupAccessibility() {
    const menu = document.querySelector('.mega-menu-navigation');
    if (!menu) return;

    // Add ARIA attributes for better accessibility
    const nav = menu.querySelector('.mega-menu-navigation__nav');
    if (nav) {
      nav.setAttribute('role', 'navigation');
      nav.setAttribute('aria-label', 'Mega Menu Navigation');
    }

    // Add skip link functionality
    this.addSkipLink();
  }

  addSkipLink() {
    const skipLink = document.createElement('a');
    skipLink.href = '#main-content';
    skipLink.textContent = 'Skip to main content';
    skipLink.className = 'skip-link';
    skipLink.style.cssText = `
      position: absolute;
      top: -40px;
      left: 6px;
      background: #000;
      color: #fff;
      padding: 8px;
      text-decoration: none;
      z-index: 10000;
      transition: top 0.3s;
    `;
    
    skipLink.addEventListener('focus', () => {
      skipLink.style.top = '6px';
    });
    
    skipLink.addEventListener('blur', () => {
      skipLink.style.top = '-40px';
    });
    
    document.body.insertBefore(skipLink, document.body.firstChild);
  }

  setupTouchEvents() {
    const menu = document.querySelector('.mega-menu-navigation');
    if (!menu) return;

    // Add touch-friendly interactions
    const links = menu.querySelectorAll('.mega-menu-navigation__link');
    links.forEach(link => {
      link.addEventListener('touchstart', this.handleTouchStart.bind(this));
      link.addEventListener('touchend', this.handleTouchEnd.bind(this));
    });
  }

  handleTouchStart(event) {
    // Add touch feedback
    event.target.style.transform = 'scale(0.98)';
  }

  handleTouchEnd(event) {
    // Remove touch feedback
    event.target.style.transform = '';
  }

  handleKeyboardNavigation(event) {
    const menu = document.querySelector('.mega-menu-navigation');
    if (!menu) return;

    // Handle arrow key navigation
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigateWithArrowKeys(event.key);
    }

    // Handle Enter key for links
    if (event.key === 'Enter' && event.target.classList.contains('mega-menu-navigation__link')) {
      event.target.click();
    }
  }

  navigateWithArrowKeys(direction) {
    const menu = document.querySelector('.mega-menu-navigation');
    if (!menu) return;

    const focusableElements = menu.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    const currentIndex = Array.from(focusableElements).findIndex(el => el === document.activeElement);
    
    if (currentIndex === -1) return;

    let nextIndex;
    if (direction === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % focusableElements.length;
    } else {
      nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    }

    focusableElements[nextIndex].focus();
  }

  handleResize() {
    this.updateResponsiveClasses();
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Public method to refresh the menu
  refresh() {
    this.updateResponsiveClasses();
  }

  // Public method to destroy the instance
  destroy() {
    // Remove event listeners if needed
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('keydown', this.handleKeyboardNavigation);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if mega menu navigation exists
  const megaMenu = document.querySelector('.mega-menu-navigation');
  if (megaMenu) {
    window.megaMenuNavigation = new MegaMenuNavigation();
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MegaMenuNavigation;
}
